import React, { useEffect } from 'react';
import moment from 'moment-timezone';
import {
  Form,
  FormInstance,
  DateSerialized,
  DateInput,
  SelectBooleanInput
} from 'blueprint-form';
import { Row, Col } from '../../../../grid';
import { EndDate } from '../date';
import SelectMethod from '../method';
import SelectLanguage from '../language';
import SelectTime from '../time';
import Weekday from '../weekday';
import Timezone from '../timezone';
import MultiTextArea from '../../multi-textarea';
import { CardBlock, CardBreakout } from '../../card';
import Callout from '../../callout';
import { GetCurrentTZ } from '../../../../libs/time';
import { useTerms } from '../../../../contexts/terms';

interface EpxDischargeHTData {
  method: string;
  language: string;
  start: string;
  end?: string;
  tz: string;
  times: string[];
  days: number[];
  dischargeDate?: string;
  sunsetMessages?: string[];
  isNewborn?: boolean;
}

type EpxDischargeHTDataRaw = DateSerialized<
  EpxDischargeHTData,
  'start' | 'end' | 'dischargeDate'
>;

const DATE_FORMAT = 'YYYY-MM-DD';
export const EpxDischargeHTDefaultData: EpxDischargeHTData = {
  method: 'message',
  language: 'en',
  dischargeDate: moment().subtract(1, 'day').format(DATE_FORMAT),
  start: moment().format(DATE_FORMAT),
  end: moment().add(1, 'month').add(1, 'day').format(DATE_FORMAT),
  tz: GetCurrentTZ(),
  times: ['12:00'],
  days: [1, 4],
  sunsetMessages: [''],
  isNewborn: false
};
const { DeserializeDate, SerializeDate } = FormInstance;

interface EpxDischargeHTFormProps {
  setForm?: (form: FormInstance<EpxDischargeHTDataRaw>) => void;
  onSubmit?: (data: EpxDischargeHTData) => void;
  isDisabled?: boolean;
}

const EpxDischargeHTForm = ({
  setForm,
  onSubmit: _onSubmit,
  isDisabled
}: EpxDischargeHTFormProps): JSX.Element => {
  const { terms } = useTerms();
  const serialize = (data: EpxDischargeHTDataRaw): EpxDischargeHTData => {
    return {
      ...data,
      dischargeDate: SerializeDate(data.dischargeDate, DATE_FORMAT),
      start: SerializeDate(data.start, DATE_FORMAT),
      end: SerializeDate(data.end, DATE_FORMAT),
      sunsetMessages: data.sunsetMessages || null
    };
  };
  const deserialize = (data: EpxDischargeHTData): EpxDischargeHTDataRaw => {
    return {
      ...data,
      dischargeDate: DeserializeDate(data.dischargeDate, DATE_FORMAT),
      start: DeserializeDate(data.start, DATE_FORMAT),
      end: DeserializeDate(data.end, DATE_FORMAT),
      sunsetMessages: data.sunsetMessages || ['']
    };
  };
  const defaultData = deserialize(EpxDischargeHTDefaultData);
  const form = new FormInstance<EpxDischargeHTDataRaw, EpxDischargeHTData>(
    defaultData,
    {
      serialize,
      deserialize
    }
  );
  useEffect(() => {
    if (setForm) {
      setForm(form);
    }
  }, []);
  const onSubmit = (data: EpxDischargeHTData): void => {
    _onSubmit(data);
  };
  const calloutMessage = (
    <>
      These messages will be sent to the {terms.patient.default} on their last
      day in the program, only if the patient\'s prescription has not been
      removed and the patient has not opted out before then. The messages must
      each be less than 160 characters. <strong>Note:</strong> this is only
      offered for text messages at this time.
    </>
  );
  return (
    <CardBlock bare>
      <Form form={form} onSubmit={onSubmit} disabled={isDisabled}>
        {(props) => {
          return (
            <>
              <CardBlock>
                <Row>
                  <Col sm={12}>
                    <SelectMethod />
                  </Col>
                  <Col sm={12}>
                    <SelectLanguage />
                  </Col>
                </Row>
              </CardBlock>
              <CardBlock>
                <Row>
                  <Col sm={12}>
                    <DateInput label="Start Date" name="start" fill />
                  </Col>
                  <Col sm={12}>
                    <EndDate label="End Date" name="end" />
                  </Col>
                  <Col sm={12}>
                    <DateInput
                      label="Discharge Date"
                      name="dischargeDate"
                      fill
                    />
                  </Col>
                  <Col sm={12}>
                    <SelectTime label="Message At" name="times[0]" />
                  </Col>
                  <Col sm={12}>
                    <Timezone label="Timezone" name="tz" fill />
                  </Col>
                </Row>
              </CardBlock>
              <CardBreakout>
                <Row>
                  <Col sm={24}>
                    <Weekday
                      label="Preferred Days (Pick At Least 1, Preferably 2 Days of Week)"
                      minItems={1}
                      inline
                      abbreviated
                    />
                  </Col>
                </Row>
              </CardBreakout>
              <CardBlock>
                <Row>
                  <Col sm={24}>
                    <SelectBooleanInput
                      label="Ask Newborn"
                      name="isNewborn"
                      fill
                    />
                    <Callout>
                      Saying 'Yes' to this means the {terms.patient.default}{' '}
                      will be sent questions pertaining to their newborn instead
                      of the standard messaging.
                    </Callout>
                  </Col>
                </Row>
              </CardBlock>
              <CardBlock>
                <MultiTextArea
                  formProps={props}
                  texts={props.values.sunsetMessages}
                  customDataValue="sunsetMessages"
                  customTitle="Custom Sunset Messages"
                  customCallout={calloutMessage}
                />
              </CardBlock>
            </>
          );
        }}
      </Form>
    </CardBlock>
  );
};

export default EpxDischargeHTForm;
